/**
 * PostHog Attribution Utility for Web
 * Handles extraction and tracking of URL parameters for attribution and campaign tracking
 *
 * PostHog automatically captures standard UTM parameters (utm_*) and click IDs (gclid, fbclid, msclkid).
 * This utility captures additional Google Ads parameters (gad_source, gad_campaignid) and other
 * custom attribution parameters that PostHog doesn't capture automatically.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import logger from './logger.js';
import supabase from './supabaseClient.js';

// AsyncStorage key for GCLID (used by GTM for conversion attribution)
const GCLID_STORAGE_KEY = 'attribution_gclid';
// AsyncStorage key for the ISO timestamp when the GCLID was first captured.
// Read by paddle.js + activate-trial wiring so server-side conversions can
// rebuild the "within 90 days" GCLID freshness window.
const GCLID_CAPTURED_AT_STORAGE_KEY = 'attribution_gclid_captured_at';
// AsyncStorage key for the full Step 7b attribution payload — a consent-gated
// superset (click-ids + utm_* + referrer + landing_path) consumed by the
// record_signup_attribution RPC. Two-tier gated at storage time so
// non-consented fields never persist. The dedicated GCLID keys above remain the
// source of truth for the gclid shared with paddle.js / activate-trial.
const ATTRIBUTION_PAYLOAD_STORAGE_KEY = 'attribution_payload';

// Module-scope guard for the late-consent re-init listener (Task 23). Prevents
// repeat registration when initializeAttribution() is called more than once
// per page load (e.g. on navigation re-mounts of a posthog provider).
let _lateConsentListenerInstalled = false;

// First-touch, IN-MEMORY snapshot of the attribution params extracted from the
// LANDING url the first time initializeAttribution() runs. Held in memory only
// (never persisted or transmitted until a consent gate passes), so it stays
// GDPR-compliant pre-consent. The late-consent listener replays THIS snapshot
// instead of re-reading window.location.href, which the landing -> /onboarding
// CTA redirect (LandingScreen.handleCtaPress) may already have stripped of
// utm_*/gclid by the time the user actions the cookie banner.
let _landingSnapshot = null;

/**
 * Remember the first non-empty attribution params seen from the landing URL.
 * First-touch wins: once captured, a later re-init on a stripped URL (e.g.
 * /onboarding?intent=...) must not clobber it. In-memory only — nothing is
 * persisted here, so it is safe to run before consent is granted.
 *
 * @param {Object} data - extracted attribution params (from extractAttributionParams)
 */
function rememberLandingSnapshot(data = {}) {
  if (typeof window === 'undefined') return;
  if (_landingSnapshot) return; // first-touch already captured
  if (!data || Object.keys(data).length === 0) return; // nothing worth keeping
  _landingSnapshot = { ...data };
}

/**
 * Returns true iff CookieYes is initialized, the user has actioned the banner,
 * AND the "advertisement" consent category is granted. Safe in non-browser
 * environments (returns false). Matches the readCkyState pattern from
 * app/_layout.tsx around lines 744-746.
 *
 * Used as the consent gate for any code path that writes GCLID to AsyncStorage,
 * forwards GCLID to Paddle customData, or sends GCLID to the activate-trial
 * edge function. Required for CookieYes / GDPR compliance.
 *
 * @returns {boolean}
 */
export function hasAdvertisementConsent() {
  if (typeof window === 'undefined') return false;
  try {
    const cky = window.getCkyConsent?.();
    return cky?.isUserActionCompleted === true && cky?.categories?.advertisement === true;
  } catch {
    return false;
  }
}

/**
 * Persist the current CookieYes advertisement-consent answer onto the signed-in
 * user's row via the set_advertisement_consent RPC. Used to build a lawful,
 * account-linked Google Customer Match audience (privacy policy 5.18).
 *
 * No-op outside a browser or when there is no active session (an anonymous
 * visitor has no account row to write to). Captures both grant (true) and
 * withdrawal (false). Fully guarded: never throws into its caller.
 *
 * @param {boolean} [explicitConsent] - authoritative consent value to write.
 *   Pass this from the cookieyes_consent_update listener (derived from
 *   event.detail.accepted), because getCkyConsent() can still report the STALE
 *   pre-change state at the instant the event fires. When omitted (e.g. the
 *   login path, where the banner action completed long ago), we read the live
 *   getCkyConsent() state via hasAdvertisementConsent().
 * @returns {Promise<void>}
 */
export async function persistAdvertisementConsent(explicitConsent) {
  if (typeof window === 'undefined') return;
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.user) return; // anonymous visitor: nothing to link to
    const consent =
      typeof explicitConsent === 'boolean' ? explicitConsent : hasAdvertisementConsent();
    const { error } = await supabase.rpc('set_advertisement_consent', { p_consent: consent });
    if (error) {
      logger.warn('[Consent] set_advertisement_consent RPC failed:', error.message);
    }
  } catch (e) {
    logger.warn('[Consent] persistAdvertisementConsent threw:', e);
  }
}

/**
 * Step 7b lighter gate. Returns true iff CookieYes is initialized, the user has
 * actioned the banner, AND the "analytics" consent category is granted. Safe in
 * non-browser environments (returns false). Mirrors hasAdvertisementConsent;
 * the category key matches app/_layout.tsx (state.categories?.analytics).
 *
 * Gates the storage + send of UTM params, referrer, landing_path, and the
 * derived attribution_source (incl. 'direct'). Click-ids stay on the heavier
 * advertisement gate.
 *
 * @returns {boolean}
 */
export function hasAnalyticsConsent() {
  if (typeof window === 'undefined') return false;
  try {
    const cky = window.getCkyConsent?.();
    return cky?.isUserActionCompleted === true && cky?.categories?.analytics === true;
  } catch {
    return false;
  }
}

/**
 * Attribution parameters to track
 * Note: Standard UTM parameters are automatically captured by PostHog, but we include them
 * here for completeness and to ensure they're properly set as person properties.
 */
const ATTRIBUTION_PARAMS = [
  // Google Ads (standard)
  'gclid',

  // Google Ads (newer parameters not automatically captured by PostHog)
  'gad_source',
  'gad_campaignid',

  // Standard UTM parameters (automatically captured by PostHog, but included for completeness)
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',

  // Facebook
  'fbclid',

  // Microsoft (Bing) Ads
  'msclkid',

  // Other common parameters
  'ref',
  'referrer',
];

/**
 * Extracts attribution parameters from a URL (web-specific)
 * @param {string} url - The URL to parse (defaults to window.location.href)
 * @returns {Object} Object containing extracted attribution parameters
 */
export function extractAttributionParams(url = typeof window !== 'undefined' ? window.location.href : '') {
  if (!url) {
    return {};
  }

  try {
    const urlObj = new URL(url);
    const queryParams = urlObj.searchParams;

    // Filter to only include attribution parameters
    const attributionData = {};

    ATTRIBUTION_PARAMS.forEach(param => {
      const value = queryParams.get(param);
      if (value) {
        attributionData[param] = value;
      }
    });

    return attributionData;
  } catch (error) {
    logger.error('[PostHog Attribution] Error extracting attribution params:', error);
    return {};
  }
}

/**
 * Stores GCLID in AsyncStorage for later use by GTM conversion tracking
 * This ensures GCLID is available even after navigation away from landing page
 * @param {string} gclid - The Google Click ID to store
 */
async function storeGclidForConversions(gclid) {
  if (!gclid) return;

  try {
    await AsyncStorage.setItem(GCLID_STORAGE_KEY, gclid);
    // Also record when we first captured the GCLID; downstream conversion
    // flows (paddle.js customData, activate-trial body) forward this so the
    // server can enforce the 90-day GCLID validity window. Best-effort: a
    // failure here does not invalidate the GCLID storage above.
    await AsyncStorage.setItem(GCLID_CAPTURED_AT_STORAGE_KEY, new Date().toISOString());
    // Never log the raw GCLID value (NFR Security). Boolean breadcrumb only.
    logger.log('[PostHog Attribution] Stored GCLID for conversion tracking', { has_gclid: true });
  } catch (error) {
    logger.error('[PostHog Attribution] Failed to store GCLID:', error);
  }
}

/**
 * Retrieves stored GCLID from AsyncStorage
 * Used by GTM helpers to include GCLID with conversion events
 * @returns {Promise<string|null>} The stored GCLID or null if not found
 */
export async function getStoredGclid() {
  try {
    const gclid = await AsyncStorage.getItem(GCLID_STORAGE_KEY);
    return gclid;
  } catch (error) {
    logger.error('[PostHog Attribution] Failed to retrieve GCLID:', error);
    return null;
  }
}

/**
 * Retrieves the ISO 8601 timestamp the GCLID was first stored. Returns null
 * when no GCLID has been captured or the captured-at write failed.
 *
 * @returns {Promise<string|null>}
 */
export async function getStoredGclidCapturedAt() {
  try {
    const v = await AsyncStorage.getItem(GCLID_CAPTURED_AT_STORAGE_KEY);
    return v;
  } catch (error) {
    logger.error('[PostHog Attribution] Failed to retrieve GCLID captured_at:', error);
    return null;
  }
}

// Step 7b: which extracted params belong to each consent tier.
const CLICK_ID_KEYS = ['gclid', 'fbclid', 'msclkid', 'gad_source', 'gad_campaignid'];
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

/**
 * Step 7b: persist a consent-gated attribution payload to AsyncStorage for
 * record_signup_attribution. Two-tier gate applied AT STORAGE TIME so
 * non-consented fields never touch storage:
 *   - advertisement consent → click-ids (gclid, fbclid, msclkid, gad_*)
 *   - analytics consent     → utm_*, referrer (query-stripped), landing_path
 * First-touch preserving: merges with any existing payload, existing values win.
 * Never logs raw values (keys only).
 *
 * @param {Object} data - extracted attribution params (from extractAttributionParams)
 */
async function storeConsentGatedPayload(data = {}) {
  if (typeof window === 'undefined') return;
  try {
    const adOK = hasAdvertisementConsent();
    const analyticsOK = hasAnalyticsConsent();
    if (!adOK && !analyticsOK) return; // nothing may be stored

    const next = {};
    if (adOK) {
      CLICK_ID_KEYS.forEach((k) => {
        if (data[k]) next[k] = data[k];
      });
    }
    if (analyticsOK) {
      UTM_KEYS.forEach((k) => {
        if (data[k]) next[k] = data[k];
      });
      // Real HTTP referrer takes precedence over a ?referrer= param. Strip
      // query + fragment client-side (the RPC strips again, defense in depth).
      const rawRef = data.referrer || (typeof document !== 'undefined' ? document.referrer : '');
      if (rawRef) next.referrer = String(rawRef).split('?')[0].split('#')[0];
      if (window.location?.pathname) next.landing_path = window.location.pathname;
    }
    if (Object.keys(next).length === 0) return;

    const existingRaw = await AsyncStorage.getItem(ATTRIBUTION_PAYLOAD_STORAGE_KEY);
    // existing wins → first-touch preserved; newly-consented fields still added.
    const merged = existingRaw ? { ...next, ...JSON.parse(existingRaw) } : next;
    if (!merged.captured_at) merged.captured_at = new Date().toISOString();
    await AsyncStorage.setItem(ATTRIBUTION_PAYLOAD_STORAGE_KEY, JSON.stringify(merged));
    logger.log('[PostHog Attribution] Stored attribution payload', { keys: Object.keys(merged) });
  } catch (error) {
    logger.error('[PostHog Attribution] Failed to store attribution payload:', error);
  }
}

/**
 * Step 7b: retrieve the stored attribution payload (or null).
 * @returns {Promise<Object|null>}
 */
export async function getStoredAttribution() {
  try {
    const raw = await AsyncStorage.getItem(ATTRIBUTION_PAYLOAD_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    logger.error('[PostHog Attribution] Failed to retrieve attribution payload:', error);
    return null;
  }
}

/**
 * Step 7b: send signup-time attribution to the server via the write-once
 * record_signup_attribution RPC. Called on the first authenticated session
 * (AuthProvider SIGNED_IN) and by the late-consent listener. Non-blocking,
 * idempotent (DB COALESCE), never throws into the caller.
 *
 * Two-tier send gate (re-checked at send time, in case consent changed):
 *   - advertisement consent → click-ids
 *   - analytics consent     → utm_*, referrer, landing_path
 * Direct traffic (analytics consent, empty payload) still calls the RPC with
 * {} so it writes attribution_source='direct'. With only advertisement consent
 * and no click-ids captured, it skips (source stays NULL — 'direct' is
 * analytics-gated). No raw values are logged.
 *
 * @param {Object} posthog - PostHog instance (optional; for breadcrumb events)
 */
export async function recordSignupAttribution(posthog) {
  if (typeof window === 'undefined') return;
  // Kill-switch (default ON): flip the PostHog flag 'signup_attribution_capture'
  // to false to disable capture without a client redeploy. Undefined / not-yet-
  // created / flags-not-loaded all evaluate to ON, so a late-loading flag never
  // drops the one-shot SIGNED_IN capture. Staged rollout is achieved by client
  // deployment (DEV first, then PROD); the flag is the emergency off-switch.
  if (posthog?.isFeatureEnabled?.('signup_attribution_capture') === false) return;
  try {
    // RPC is authenticated-only (auth.uid()); skip silently when no session.
    const { data: { session } = {} } = await supabase.auth.getSession();
    if (!session) return;

    const adOK = hasAdvertisementConsent();
    const analyticsOK = hasAnalyticsConsent();
    if (!adOK && !analyticsOK) {
      posthog?.capture('signup_attribution_skipped_no_consent');
      return;
    }

    const stored = (await getStoredAttribution()) || {};
    const p = {};

    if (adOK) {
      // gclid + captured_at from the dedicated keys (source of truth shared with
      // paddle.js / activate-trial), falling back to the payload.
      const gclid = (await getStoredGclid()) || stored.gclid || null;
      if (gclid) {
        p.gclid = gclid;
        const gclidAt = (await getStoredGclidCapturedAt()) || stored.gclid_captured_at || null;
        if (gclidAt) p.gclid_captured_at = gclidAt;
      }
      ['fbclid', 'msclkid', 'gad_source', 'gad_campaignid'].forEach((k) => {
        if (stored[k]) p[k] = stored[k];
      });
    }

    if (analyticsOK) {
      UTM_KEYS.forEach((k) => {
        if (stored[k]) p[k] = stored[k];
      });
      if (stored.referrer) p.referrer = stored.referrer;
      if (stored.landing_path) p.landing_path = stored.landing_path;
    }

    // Only advertisement consent + no click-ids → nothing to write
    // (attribution_source='direct' requires analytics consent).
    if (Object.keys(p).length === 0 && !analyticsOK) {
      posthog?.capture('signup_attribution_skipped_no_consent', { reason: 'no_data' });
      return;
    }

    const { error } = await supabase.rpc('record_signup_attribution', { p_attribution: p });
    if (error) {
      logger.error('[PostHog Attribution] record_signup_attribution failed (non-blocking):', error.message);
      posthog?.capture('signup_attribution_error', { message: error.message });
      return;
    }
    // Breadcrumb only — booleans, never raw identifiers.
    posthog?.capture('signup_attribution_recorded', {
      has_gclid: !!p.gclid,
      has_utm: !!p.utm_source,
    });
  } catch (error) {
    logger.error('[PostHog Attribution] recordSignupAttribution threw (non-blocking):', error);
  }
}

/**
 * Tracks attribution data with PostHog
 * @param {Object} posthog - PostHog instance
 * @param {Object} attributionData - Attribution parameters to track
 */
export function trackAttributionParams(posthog, attributionData) {
  if (!posthog || !attributionData || Object.keys(attributionData).length === 0) {
    return;
  }

  try {
    // Capture as event properties for this session
    posthog.capture('$attribution', attributionData);

    // Set as person properties for long-term attribution
    const personProperties = {};

    // Only set initial attribution if not already set
    Object.keys(attributionData).forEach(key => {
      personProperties[`initial_${key}`] = attributionData[key];
      personProperties[`latest_${key}`] = attributionData[key];
    });

    posthog.setPersonPropertiesOnce(personProperties);

    logger.log('[PostHog Attribution] Tracked attribution data:', attributionData);
  } catch (error) {
    logger.error('[PostHog Attribution] Error tracking attribution params:', error);
  }
}

/**
 * Initializes attribution tracking for web
 * Should be called once on app startup in browser environment
 * @param {Object} posthog - PostHog instance
 */
export function initializeAttribution(posthog) {
  if (typeof window === 'undefined') {
    logger.warn('[PostHog Attribution] Not in browser environment, skipping attribution tracking');
    return;
  }

  if (!posthog) {
    logger.warn('[PostHog Attribution] PostHog instance not provided');
    return;
  }

  try {
    const url = window.location.href;
    logger.log('[PostHog Attribution] Initializing with URL:', url);

    const attributionData = extractAttributionParams(url);

    // Snapshot the first-touch landing params in memory (consent-independent;
    // nothing is persisted here). The late-consent listener replays this so a
    // navigation away from the landing URL (e.g. CTA -> /onboarding, which
    // strips utm_*/gclid) before the cookie banner is actioned no longer loses
    // the attribution.
    rememberLandingSnapshot(attributionData);

    if (Object.keys(attributionData).length > 0) {
      // CookieYes consent gate: PostHog person properties + GCLID AsyncStorage
      // write are both gated on the "advertisement" category being granted
      // (Requirements 5.1, 5.2, 5.6). Without consent we still log a
      // breadcrumb so Sentry/PostHog session debugging can see the skip, but
      // we never persist or transmit attribution identifiers.
      if (hasAdvertisementConsent()) {
        trackAttributionParams(posthog, attributionData);
        logger.log('[PostHog Attribution] Successfully tracked attribution parameters');

        // Store GCLID in AsyncStorage for GTM conversion attribution
        if (attributionData.gclid) {
          storeGclidForConversions(attributionData.gclid);
        }
      } else {
        logger.log('[PostHog Attribution] Advertisement consent missing, skipping attribution write');
      }
    } else {
      logger.log('[PostHog Attribution] No attribution parameters found in URL');
    }

    // Step 7b: persist the consent-gated attribution payload (superset of the
    // dedicated gclid key) consumed by record_signup_attribution. Two-tier
    // gated + first-touch-preserving inside the helper.
    storeConsentGatedPayload(attributionData);

    // Task 23: install a one-time late-consent listener. When the user
    // accepts the advertisement category AFTER initial page load (e.g. they
    // dismissed the banner first, then accepted from the floating widget),
    // re-run initializeAttribution() iff the URL still carries a GCLID AND
    // we have not yet persisted one. Module-scope flag prevents repeated
    // registration; closure-bound posthog reference is fine because the
    // PostHog instance is a stable singleton in our app shell.
    if (typeof document !== 'undefined' && !_lateConsentListenerInstalled) {
      _lateConsentListenerInstalled = true;
      document.addEventListener('cookieyes_consent_update', async (event) => {
        try {
          const accepted = event?.detail?.accepted;
          const list = Array.isArray(accepted) ? accepted : [];
          const includesAd = list.includes('advertisement');
          const includesAnalytics = list.includes('analytics');
          // Step 7b: react to either tier being granted late.
          if (!includesAd && !includesAnalytics) return;

          // Re-capture for the newly-granted tier from the first-touch landing
          // snapshot (preferred), falling back to the current URL.
          const href = (typeof window !== 'undefined' && window.location?.href) || '';
          // First-touch landing snapshot wins: by now the CTA redirect to
          // /onboarding may have stripped utm_*/gclid from the live URL. Fall
          // back to a fresh re-read for anything still present (consent actioned
          // while still on the landing page).
          const fresh = extractAttributionParams(href);
          const data = { ...fresh, ...(_landingSnapshot || {}) };
          if (includesAd && data.gclid && !(await getStoredGclid())) {
            trackAttributionParams(posthog, data);
            storeGclidForConversions(data.gclid);
          }
          await storeConsentGatedPayload(data);

          // If already authenticated, send now (idempotent; no-op if no session).
          await recordSignupAttribution(posthog);

          // Mirror the (possibly changed) advertisement-consent answer onto the
          // user row so Customer Match eligibility stays current on late grant
          // OR withdrawal. Pass includesAd from the event payload — the
          // authoritative post-change value — because getCkyConsent() may still
          // report the stale pre-change state here. No-op when no session.
          // (Privacy policy 5.18.)
          await persistAdvertisementConsent(includesAd);
        } catch (e) {
          logger.warn('[PostHog Attribution] cookieyes_consent_update listener threw:', e);
        }
      });
    }
  } catch (error) {
    logger.error('[PostHog Attribution] Error initializing attribution tracking:', error);
  }
}

/**
 * Alias for initializeAttribution for backward compatibility
 * @deprecated Use initializeAttribution instead
 * @param {Object} posthog - PostHog instance
 */
export function initializeBrowserAttribution(posthog) {
  return initializeAttribution(posthog);
}
